import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import clsx from 'clsx';
import { useRef, useState } from 'react';
import { type useExportConfig } from '../hooks/useExportConfig';

type Props = {
  fieldKey: string;
  config: ReturnType<typeof useExportConfig>;
  /** Com texto na busca a lista está filtrada: reordenar entre vizinhos visíveis mentiria sobre a ordem real. */
  reorderDisabled: boolean;
};

/**
 * Coluna do arquivo: alça de arrastar (só mouse/toque), nome editável no lugar, mover ↑ ↓ e remover. Os botões
 * de mover são o caminho de teclado: o sensor de teclado do dnd-kit rola a lista antes de mover o item.
 */
const ExportColumnRow = ({ fieldKey, config, reorderDisabled }: Props) => {
  const label = config.availableFields.find((field) => field.key === fieldKey)?.label ?? fieldKey;
  const header = config.headerOf(fieldKey);
  const headerOverride = config.headerOverrides[fieldKey] ?? '';
  const isFirst = fieldKey === config.selectedKeys[0];
  const isLast = fieldKey === config.selectedKeys[config.selectedKeys.length - 1];

  const renameButton = useRef<HTMLButtonElement>(null);
  const [editing, setEditing] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: fieldKey,
    disabled: reorderDisabled,
  });
  const inputId = `export-name-${fieldKey}`;

  const stopEditing = () => {
    // Nome só com espaços não é nome: volta ao rótulo do catálogo.
    if (!headerOverride.trim()) config.setHeaderOverride(fieldKey, '');
    setEditing(false);
  };

  return (
    <div
      ref={setNodeRef}
      className={clsx(
        'd-flex align-items-center gap-1 border rounded bg-body px-2 py-1 mb-2',
        isDragging ? 'border-primary shadow-sm' : 'border-gray-300',
      )}
      style={{ transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 1 : undefined }}
    >
      <button
        aria-label={`Arrastar ${label}`}
        className="btn btn-sm btn-icon w-25px h-25px cursor-grab"
        type="button"
        {...attributes}
        {...listeners}
        // Arrastar é só de ponteiro: a alça sai do Tab e não anuncia instruções de teclado que não valem aqui.
        aria-describedby={undefined}
        disabled={reorderDisabled}
        tabIndex={-1}
      >
        <i className="bi bi-grip-vertical text-gray-500 p-0" />
      </button>
      {editing ? (
        <>
          <label className="visually-hidden" htmlFor={inputId}>
            Nome de {label} no arquivo
          </label>
          <input
            autoFocus
            className="form-control form-control-sm form-control-solid flex-grow-1"
            id={inputId}
            placeholder={label}
            value={headerOverride}
            onBlur={stopEditing}
            onChange={(event) => config.setHeaderOverride(fieldKey, event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== 'Escape') return;
              // Esc aqui encerra a edição; não pode subir e fechar o modal. E o Enter não pode sobrar para o
              // lápis, que recebe o foco logo abaixo: ele reabriria a edição.
              event.preventDefault();
              event.stopPropagation();
              stopEditing();
              // O input some: o foco volta para o lápis em vez de cair no corpo da página.
              renameButton.current?.focus();
            }}
          />
        </>
      ) : (
        <span className="flex-grow-1 overflow-hidden text-truncate fs-7">
          <span className="fw-semibold text-gray-900">{header}</span>
          {header !== label ? <span className="text-gray-500 ms-2">era {label}</span> : null}
        </span>
      )}
      <button
        ref={renameButton}
        aria-label={`Renomear ${label}`}
        className="btn btn-sm btn-icon btn-active-light-primary w-25px h-25px"
        title="Renomear no arquivo"
        type="button"
        onClick={() => setEditing(true)}
      >
        <i className="bi bi-pencil p-0" />
      </button>
      <button
        aria-label={`Mover ${label} para cima`}
        className="btn btn-sm btn-icon btn-active-light-primary w-25px h-25px"
        disabled={reorderDisabled || isFirst}
        type="button"
        onClick={() => config.moveColumn(fieldKey, -1)}
      >
        <i className="bi bi-arrow-up p-0" />
      </button>
      <button
        aria-label={`Mover ${label} para baixo`}
        className="btn btn-sm btn-icon btn-active-light-primary w-25px h-25px"
        disabled={reorderDisabled || isLast}
        type="button"
        onClick={() => config.moveColumn(fieldKey, 1)}
      >
        <i className="bi bi-arrow-down p-0" />
      </button>
      <button
        aria-label={`Remover ${label}`}
        className="btn btn-sm btn-icon btn-active-light-danger w-25px h-25px"
        title="Tirar do arquivo"
        type="button"
        onClick={() => config.toggleColumn(fieldKey)}
      >
        <i className="bi bi-x fs-4 p-0" />
      </button>
    </div>
  );
};

export default ExportColumnRow;
